import React, { useState } from 'react';
import { useDebounce } from 'react-use';

import { Formik } from 'formik';

import { Card, DialogActions, DialogContent, Typography, Box } from '@mui/material';

import { useTranslation } from 'next-i18next';

import Button from 'src/components/ui/Button';
import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Loader from 'src/components/ui/Loader';
import TinyPreviewCard from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import Icon from 'src/components/ui/Icon';
import TextField from 'src/components/ui/TextField';
import EmptyState from 'src/components/ui/EmptyState';

import { colours } from 'src/constants/colours';
import { isBottomDiv } from 'src/utils/isBottom';

import { useSearchProjectsQuery } from 'src/hooks/__generated__/queries';
import type { Project, User, Company, SearchProjectsQuery, Medium } from 'src/types/__generated__/graphql';

import type { ModalProps } from 'src/hooks/useModalOpenState';
import { styledComponent, styledTag } from 'src/utils/styled';

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        width: 30rem;
        max-height: 35.5rem;
    }
    .MuiDialogTitle-root {
        padding: 1.25rem 1.5rem 0.75rem 1.5rem;
    }
    .MuiDialogActions-root {
        padding: 0.5rem 1.5rem 1.5rem 1.5rem;
    }
`;

const StyledDialogContent = styledComponent(DialogContent)`
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
`;

const StyledTextField = styledComponent(TextField)`
    margin: 1rem 1rem 0.5rem;
`;

const ScrollContainer = styledTag('div')`
    overflow-y: auto;
`;

const StyledCard = styledComponent(Card)<{
    $selected: boolean;
}>`
    ${({ $selected }) =>
        $selected &&
        `
        background-color: ${colours.secondary.O12};
    `}
    :not(:first-of-type) {
        margin-top: 0.7rem;
    }
    :last-of-type {
        margin-bottom: 0.7rem;
    }
    box-shadow: 0rem 0.1rem 1rem -0.5rem ${colours.blackOpacity.O16};
    position: relative;
`;

type Props = {
    onSelection: (projectId: Project['id']) => void;
    open: ModalProps['open'];
    onClose: ModalProps['onClose'];
    userId?: User['id'];
    companyId?: Company['id'];
};

const SelectProjectModal: React.FC<Props> = ({ onSelection, open, onClose, userId, companyId }) => {
    const { t } = useTranslation(['news', 'project', 'community', 'common']);
    const [selectedProjectdId, setSelectedProjectdId] = useState<Project['id']>();
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');

    useDebounce(() => setDebouncedQuery(query), 500, [query]);

    const {
        data: projects,
        loading: searchLoading,
        fetchMore: fetchMoreProjects,
    } = useSearchProjectsQuery({
        variables: {
            search: debouncedQuery.length > 0 ? debouncedQuery : null,
            offset: 0,
            limit: 10,
            userId,
            companyId,
            orderBy: { createdAt: 'desc' },
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    const handleSelectProject = (projectId: Project['id']) => {
        if (projectId !== selectedProjectdId) {
            setSelectedProjectdId(projectId);
        } else {
            setSelectedProjectdId(undefined);
        }
    };

    const handleClose = () => {
        setSelectedProjectdId(undefined);
        setQuery('');
        onClose();
    };

    const handleSubmit = () => {
        if (selectedProjectdId) {
            onSelection(selectedProjectdId);
        }
        handleClose();
    };

    const projectsTotalCount = projects?.searchProjects?.total ?? 0;

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const target = ev.target as HTMLDivElement;
        const bottom = isBottomDiv(target);
        const projectsActualCount = projects?.searchProjects?.results.length ?? 0;
        const allDataLoaded = projectsActualCount >= projectsTotalCount;
        if (bottom && projectsActualCount && !allDataLoaded && !searchLoading) {
            await fetchMoreProjects({
                variables: {
                    offset: projectsActualCount,
                },
                updateQuery(prev, { fetchMoreResult }: { fetchMoreResult?: SearchProjectsQuery }) {
                    if (!fetchMoreResult) return prev;
                    const prevItems = prev.searchProjects?.results ?? [];
                    const newItems = fetchMoreResult.searchProjects?.results ?? [];

                    return {
                        searchProjects: {
                            total: projectsTotalCount,
                            results: [...prevItems, ...newItems],
                        },
                    };
                },
            });
        }
    };

    const mapProjectToCard = (project: Pick<Project, 'id' | 'name' | 'media'>) => (
        <StyledCard
            key={project.id}
            data-target="project-modal-card"
            data-target-id={`project-modal-card-${project.id}`}
            elevation={0}
            onClick={() => handleSelectProject(String(project.id))}
            $selected={project.id === selectedProjectdId}
        >
            <TinyPreviewCard
                iconName="ProjectIcon"
                type="project"
                title={t('news:project')}
                media={project.media as Medium[]}
                subtitle={project.name}
            />
        </StyledCard>
    );

    return (
        <StyledDialog open={open} onClose={handleClose}>
            <DialogTitle onClose={handleClose}>
                <Typography variant="h3">{t('news:linkProject')}</Typography>
            </DialogTitle>

            {/* Formik is used here to avoid errors using useField in children components */}
            <Formik
                initialValues={{
                    query: '',
                }}
                onSubmit={console.info}
            >
                {() => (
                    <Box display="flex">
                        <StyledTextField
                            name="query"
                            placeholder={t('news:searchProject')}
                            InputProps={{
                                startAdornment: <Icon name="Search" color={colours.primary.O60} />,
                            }}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </Box>
                )}
            </Formik>

            <StyledDialogContent>
                <ScrollContainer onScroll={handleScroll}>
                    {Boolean(projects?.searchProjects?.results.length) &&
                        projects?.searchProjects?.results.map(
                            (project) => project && mapProjectToCard(project as Project),
                        )}

                    {searchLoading && (
                        <Box height="3rem" width="100%">
                            <Loader />
                        </Box>
                    )}
                    {Boolean(!projects?.searchProjects?.results.length) && !searchLoading && (
                        <Box mt="1rem">
                            <EmptyState icon="SearchPerson" text1={t('common:noSearchResult')} />
                        </Box>
                    )}
                </ScrollContainer>
            </StyledDialogContent>
            <DialogActions>
                <Button
                    data-target="project-modal-button"
                    data-target-id="project-modal-cancel"
                    variant="outlined"
                    onClick={handleClose}
                >
                    {t('common:cancel')}
                </Button>
                <Button onClick={handleSubmit} disabled={!selectedProjectdId}>
                    {t('news:link')}
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default SelectProjectModal;
